import { api, get } from "../api.ts";
import { isAdmin, state, wsBase } from "../state.ts";
import type { PersonDetail } from "../types.ts";
import { rerender } from "../rerender.ts";
import { append, busy, confirmDialog, h, toast, trackLine, when } from "../ui.ts";

function list<T>(rows: T[], line: (r: T) => string, empty: string) {
  if (rows.length === 0) return h("p", { class: "empty" }, empty);
  const ul = h("ul", { class: "plain" });
  for (const r of rows) ul.append(h("li", null, line(r)));
  return ul;
}

function words(items: string[], empty: string, cls = "") {
  if (items.length === 0) return h("p", { class: "muted" }, empty);
  const box = h("div", { class: "chips" });
  for (const w of items) box.append(h("span", { class: `chip ${cls}` }, w));
  return box;
}

export async function renderPerson(root: HTMLElement, id: string) {
  const d = await get<PersonDetail>(`${wsBase()}/people/${encodeURIComponent(id)}`);
  const p = d.person;
  const goal = d.profile?.goal_role
    ? `${d.profile.goal_role}${d.profile.target_date ? ` by ${when(d.profile.target_date)}` : ""}`
    : "No goal set yet";
  const levels = d.profile ? Object.entries(d.profile.levels).map(([k, v]) => `${k}: ${v}`).join(", ") : "";

  const actions = h("div", { class: "actions" });
  const remind = h("button", { type: "button" }, "Send a reminder") as HTMLButtonElement;
  remind.addEventListener("click", () =>
    busy(remind, async () => {
      const r = await api<{ sent: number }>("POST", `${wsBase()}/reminders`, { user_ids: [id] });
      toast(r.sent ? `Reminder sent to ${p.email}.` : "No reminder sent: this person is suspended.");
    }),
  );
  actions.append(remind);
  if (isAdmin() && p.role !== "owner" && p.user_id !== state.overview?.me.user_id) {
    const s = h("button", { type: "button", class: p.suspended ? "primary" : "danger" }, p.suspended ? "Restore access" : "Suspend access") as HTMLButtonElement;
    s.addEventListener("click", () =>
      busy(s, async () => {
        if (!p.suspended) {
          const ok = await confirmDialog(
            `Suspend ${p.name ?? p.email}?`,
            "They can't use the company workspace until you restore them. Their training history stays, and their seat is freed.",
            "Suspend access",
            true,
          );
          if (!ok) return;
        }
        await api("POST", `${wsBase()}/members/${encodeURIComponent(id)}/${p.suspended ? "restore" : "suspend"}`);
        toast(p.suspended ? "Access restored." : "Access suspended.");
        state.overview = null;
        rerender();
      }),
    );
    actions.append(s);
  }

  const tracks = h("div", { class: "stack" });
  if (d.tracks.length === 0) tracks.append(h("p", { class: "empty" }, "No track yet. Give one on the Tracks page."));
  for (const t of d.tracks) {
    const due = t.assignment?.due_date ? `Due ${when(t.assignment.due_date)}${t.assignment.overdue ? ", overdue" : ""}` : t.assignment ? "No due date" : "Chosen by them";
    tracks.append(
      h(
        "div",
        { class: "track-block" },
        h("div", { class: "track-name" }, t.title, h("span", { class: "muted" }, ` ${t.modules_done} of ${t.modules_total} modules`)),
        trackLine(t.modules),
        h("p", { class: t.assignment?.overdue ? "late small" : "muted small" }, `${due}${t.current ? `. Now on ${t.current.id}: ${t.current.title}` : ""}`),
      ),
    );
  }

  append(
    root,
    h("p", null, h("a", { href: "#/overview", class: "back" }, "All people")),
    h(
      "header",
      { class: "page-head" },
      h(
        "div",
        null,
        h("h1", null, p.name ?? p.email),
        h("p", { class: "muted" }, [p.email, p.groups.map((g) => g.path).join(", "), p.suspended ? `suspended ${when(p.suspended_at)}` : ""].filter(Boolean).join(". ")),
      ),
      actions,
    ),
    h(
      "section",
      { class: "summary" },
      h("p", { class: "lede" }, goal),
      levels && h("p", { class: "muted" }, levels),
      h(
        "dl",
        { class: "figures" },
        h("div", null, h("dt", null, "Last session"), h("dd", null, when(p.last_active))),
        h("div", null, h("dt", null, "This week"), h("dd", null, `${p.minutes_week} min`)),
        h("div", null, h("dt", null, "Labs done"), h("dd", null, d.labs_done)),
        h("div", null, h("dt", null, "Practice problems"), h("dd", null, d.practice_done)),
        h("div", null, h("dt", null, "Credits this month"), h("dd", null, d.credits_this_month)),
      ),
    ),
    h("section", null, h("h2", null, "Tracks"), tracks),
    h(
      "div",
      { class: "two" },
      h("section", null, h("h2", null, "Checkpoint scores"), list(d.scores.slice().reverse(), (s) => `${when(s.at)}  ${s.title}: ${s.score}`, "No checkpoints yet.")),
      h("section", null, h("h2", null, "Mock interviews"), list(d.interviews.slice().reverse(), (s) => `${when(s.at)}  ${s.role}: ${s.score ?? "–"}`, "No mock interviews yet.")),
    ),
    h(
      "div",
      { class: "two" },
      h("section", null, h("h2", null, "Needs work"), words(d.weak, "Nothing noted yet.", "warn")),
      h("section", null, h("h2", null, "Strong at"), words(d.strong, "Nothing noted yet.", "good")),
    ),
    h(
      "section",
      null,
      h("h2", null, "Sessions in the last 30 days"),
      list(d.sessions, (s) => `${when(s.date)}  ${s.minutes} min${s.summary ? `. ${s.summary}` : ""}`, "No sessions in the last 30 days."),
      h("p", { class: "muted small" }, "Summaries are written by Kai for you. Conversations, screens and code are never kept."),
    ),
  );
}
